import { useQuery } from "@tanstack/react-query";
import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  ExternalLink,
  Newspaper,
  Megaphone,
  Sparkles,
  FileText,
} from "lucide-react";
import { fetchStock, fetchStockNews } from "@/lib/api";

export default function StockDetailSheet({ loser, open, onOpenChange }) {
  const ticker = loser?.ticker || null;
  const changePct = loser?.change_pct ?? null;

  const historyQ = useQuery({
    queryKey: ["stock", ticker],
    queryFn: () => fetchStock(ticker, 30),
    enabled: !!ticker && open,
  });

  const newsQ = useQuery({
    queryKey: ["news", ticker, changePct],
    queryFn: () => fetchStockNews(ticker, changePct),
    enabled: !!ticker && open,
    staleTime: 5 * 60 * 1000,
  });

  const data = historyQ.data;
  const points = data?.points || [];
  const last = points[points.length - 1]?.close;
  const first = points[0]?.close;
  const pct = first && last ? ((last - first) / first) * 100 : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="stock-detail-sheet"
        side="right"
        className="bg-[#0e0e10] border-l border-white/10 text-zinc-100 sm:max-w-lg w-full overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <div className="flex items-center gap-3">
            <SheetTitle className="font-mono text-2xl text-zinc-50">
              {ticker}
            </SheetTitle>
            {changePct != null && (
              <Badge className="rounded-md bg-[#FF3B30]/15 text-[#FF6B62] border-0 font-mono">
                {changePct.toFixed(2)}%
              </Badge>
            )}
          </div>
          <SheetDescription className="text-zinc-400">
            {data?.company_name || (historyQ.isLoading ? "Loading…" : "")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-8">
          {/* Chart & basic info */}
          <section>
            {historyQ.isLoading && (
              <div className="space-y-3">
                <div className="h-40 rounded bg-zinc-800/60 animate-pulse" />
              </div>
            )}
            {data && (
              <>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-500">
                  30-day close
                </div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="text-3xl font-mono font-medium">
                    ${last?.toFixed(2)}
                  </span>
                  {pct !== null && (
                    <span
                      className={`text-sm font-mono ${
                        pct < 0 ? "text-[#FF6B62]" : "text-emerald-400"
                      }`}
                    >
                      {pct >= 0 ? "+" : ""}
                      {pct.toFixed(2)}%
                    </span>
                  )}
                </div>
                <div className="h-44 mt-3 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <ReLineChart data={points}>
                      <CartesianGrid stroke="#1f1f22" strokeDasharray="2 4" />
                      <XAxis
                        dataKey="date"
                        hide={points.length > 10}
                        tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                        tickLine={false}
                        axisLine={{ stroke: "#27272a" }}
                      />
                      <YAxis
                        domain={["auto", "auto"]}
                        tick={{ fill: "#71717a", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                        tickLine={false}
                        axisLine={{ stroke: "#27272a" }}
                        width={44}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#121214",
                          border: "1px solid #27272a",
                          borderRadius: 6,
                          fontFamily: "IBM Plex Mono",
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#a1a1aa" }}
                        itemStyle={{ color: "#fafafa" }}
                        formatter={(v) => [`$${Number(v).toFixed(2)}`, "Close"]}
                      />
                      <Line
                        type="monotone"
                        dataKey="close"
                        stroke="#FF3B30"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: "#FF3B30" }}
                      />
                    </ReLineChart>
                  </ResponsiveContainer>
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mt-4">
                  <Info label="Sector" value={data.sector} />
                  <Info label="Industry" value={data.industry} />
                  <Info
                    label="Market Cap"
                    value={
                      data.market_cap
                        ? "$" + (data.market_cap / 1e9).toFixed(2) + "B"
                        : null
                    }
                  />
                  <Info label="Currency" value={data.currency} />
                </dl>

                {data.website && (
                  <a
                    data-testid="stock-website-link"
                    href={data.website}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-zinc-400 hover:text-zinc-100"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {data.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </>
            )}
          </section>

          <Separator className="bg-white/10" />

          {/* AI-generated summary */}
          <section data-testid="drop-summary-section">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-500">
              <Sparkles className="h-3.5 w-3.5 text-[#FF3B30]" />
              Why did it drop? — AI summary
            </div>

            {newsQ.isLoading && (
              <div className="mt-3 space-y-2">
                <div className="h-3 rounded bg-zinc-800/60 animate-pulse w-11/12" />
                <div className="h-3 rounded bg-zinc-800/60 animate-pulse w-10/12" />
                <div className="h-3 rounded bg-zinc-800/60 animate-pulse w-8/12" />
              </div>
            )}

            {newsQ.isError && (
              <p className="mt-2 text-sm text-[#FF6B62] font-mono">
                Failed to load news.
              </p>
            )}

            {newsQ.data && (
              <p
                data-testid="drop-summary-text"
                className="mt-3 text-sm leading-relaxed text-zinc-200"
              >
                {newsQ.data.summary ||
                  "No clear public explanation available in the headlines below."}
              </p>
            )}
          </section>

          {/* ASX announcements */}
          <section data-testid="announcements-section">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-500">
              <Megaphone className="h-3.5 w-3.5" />
              ASX announcements
            </div>
            {newsQ.isLoading && (
              <div className="mt-3 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-10 rounded bg-zinc-800/60 animate-pulse" />
                ))}
              </div>
            )}
            {newsQ.data &&
              (newsQ.data.announcements?.length ? (
                <ul className="mt-3 space-y-2" data-testid="announcements-list">
                  {newsQ.data.announcements.slice(0, 6).map((a, i) => (
                    <li key={i}>
                      <a
                        href={a.url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="group flex gap-3 rounded-md border border-white/10 bg-[#121214] p-3 hover:border-white/20"
                      >
                        <FileText className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 group-hover:text-white line-clamp-2">
                            {a.title}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                            <span>{a.published_at || "—"}</span>
                            {a.price_sensitive && (
                              <span className="text-[#FF6B62]">Price-sensitive</span>
                            )}
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm font-mono text-zinc-500">
                  No recent ASX announcements found.
                </p>
              ))}
          </section>

          {/* News articles */}
          <section data-testid="articles-section">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-500">
              <Newspaper className="h-3.5 w-3.5" />
              News articles
            </div>
            {newsQ.isLoading && (
              <div className="mt-3 space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 rounded bg-zinc-800/60 animate-pulse" />
                ))}
              </div>
            )}
            {newsQ.data &&
              (newsQ.data.articles?.length ? (
                <ul className="mt-3 space-y-2" data-testid="articles-list">
                  {newsQ.data.articles.slice(0, 8).map((a, i) => (
                    <li key={i}>
                      <a
                        href={a.url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="group flex gap-3 rounded-md border border-white/10 bg-[#121214] p-3 hover:border-white/20"
                      >
                        <ExternalLink className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 group-hover:text-white line-clamp-2">
                            {a.title}
                          </div>
                          {a.snippet && (
                            <div className="mt-1 text-xs text-zinc-400 line-clamp-2">
                              {a.snippet}
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                            <span>{a.publisher}</span>
                            <span>·</span>
                            <span>{fmtDate(a.published_at)}</span>
                          </div>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm font-mono text-zinc-500">
                  No related news articles found.
                </p>
              ))}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </dt>
      <dd className="mt-1 font-mono text-zinc-200 text-sm">{value || "—"}</dd>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}
